/**
 * Descendants by State — the dashboard detail page for the
 * /report-management/family-by-state report (rpt_FamByStates).
 *
 * Laid out as the report is written on paper: each state heads its own
 * block with the number living there, and under it one row per member —
 * the name, then the head of their line and its colour. No column titles,
 * and no state on the row: the heading already names it.
 *
 *     Georgia (10)
 *        John, Jr.    Mike, Sr. • Gold Line
 *
 * On a desktop the list runs in two columns the way Family Members does:
 * the first fills the visible height and the rest carries on in a second
 * column that scrolls, a state's heading repeated where the split falls
 * inside it. Each column is one grid, so its two columns line up down
 * that column and each is exactly as wide as its longest entry — a
 * `max-content` track with a fixed column-gap, as Generations & Ages lays
 * its table out, so nothing wraps; a phone never splits and scrolls one
 * column, sideways too where it runs wide, and reads as the name then the
 * line colour — with the family lines named in the footer key. See
 * `.desc-by-state-list` in App.css.
 *
 * The rows are the report endpoint's own: living members only, each row
 * already carrying its line colour and the head of their family line, filed
 * under the state the member lives in now (current_state_residing) — see
 * fetchFamilyByState. One thing moves: anyone 17 or younger whose record
 * names no current state is filed under the state their parent is listed
 * in, since a child lives with their parents — the parent and the
 * age read off /family-tree-hierarchy-v2, the same tree Desc Parent and
 * Child(ren) reads (see fileMinorsWithParents). The heading prints
 * "5 States/72 FMs" — the states listed over the members in them, once the
 * minors have moved — and the dashboard card the endpoint's own pair; on a
 * phone the heading prints the bare "5/72", since the words would not fit
 * beside the title. /dashboard-statistics-with-details is read for what
 * the report rows do not name: the name markers — the in-law asterisk, the
 * deceased angel and the multiple-birth tag — which come off each row's
 * roster record, and the date of birth the age sort reads.
 *
 * The states always read A–Z, and inside each one the members read A–Z by
 * name unless a sort says otherwise, and Line Color groups a state's rows
 * by line and reads each line A–Z. A State dropdown at the head of the
 * bar — the same one Family Gatherings filters its member list with, every state
 * named with its abbreviation ("Georgia - GA"), a search box over the list,
 * Clear and Apply under it — picks which states are listed; with none
 * ticked every state is. The filter bar is the shared report one: every
 * sort keeps the states where they are and re-orders the members inside
 * each one, so the report stays "by state" under every sort. The bar
 * carries the sorts alone — no member tabs: the endpoint lists the living
 * descendants and nothing else, so every tab only repeated the whole list
 * or emptied it. Neither age sort is on the bar: the rows carry no date of
 * birth of their own, so age is the one thing this report does not order
 * by. The search box reads every name, state and line colour on the page:
 * a match on a state's heading keeps the whole state, and otherwise only
 * the rows that match. See
 * docs/dashboard-filter-rules.md.
 */

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getApi } from "../../../Services/apiService";
import { getFamilyTreeIdFromStorage } from "../../../Utils/headOfLineRole";
import DashboardBackButton from "./DashboardBackButton";
import DashboardColorLegend from "./DashboardColorLegend";
import DashboardDetailSearchBox, {
  DashboardDetailSearchNoMatch,
} from "./DashboardDetailSearchBox";
import DashboardDetailSortFilterBar from "./DashboardDetailSortFilterBar";
import DashboardLineColorName from "./DashboardLineColorName";
import DashboardMarkerLegend from "./DashboardMarkerLegend";
import useDashboardPhoneLayout from "./useDashboardPhoneLayout";
import {
  DashboardDeceasedAngel,
  DashboardInLawAsterisk,
  DashboardMemberNameLabel,
  DashboardNameMarkerGroup,
  DashboardPhotoModal,
  DashboardPhotoName,
  getDashboardMemberNamePhotoUrls,
  useDashboardPhotoModal,
} from "./DashboardPhotoViewer";
import {
  attachDescByStateSpouses,
  countDescByStateMembers,
  fetchDescByStateFamilyTree,
  fetchFamilyByState,
  fileMinorsWithParents,
  getDescByStateTotals,
  indexFamilyTreeByMember,
  splitDescByStateBlocks,
  toDescByStateBlocks,
} from "../../../Utils/dashboardDescByState";
import {
  FilterTriggerLabel,
  filterTriggerClass,
  formatFilterValueList,
} from "../../../Utils/filterTriggerLabel";
import {
  getStateAbbreviation,
  normalizeStateName,
  stateFilterOptions,
} from "../../../constants/states";
import {
  dashboardSearchMatches,
  getDashboardMemberSearchText,
  normalizeDashboardSearchText,
} from "../../../Utils/dashboardDetailSearch";
import { getMemberDobTimestamp } from "../../../Utils/family-tree/dateAndTime";
import {
  isDashboardDeceasedMember,
  isDashboardInLawMember,
} from "../../../Utils/dashboardMemberDisplayName";
import { isExplicitDashboardStartOrTopOfLineMember } from "../../../Utils/dashboardStartOfLineMember";
import {
  getMultipleBirthLegendTags,
  getMultipleBirthSuperscriptText,
} from "../../../Utils/dashboardMultipleBirthSuperscript";
import {
  buildDashboardColorLegendLines,
  buildHeadsOfLineIdByColorMap,
  extractAllMembersFromStatisticsResponse,
  extractHeadsOfLineFromStatisticsResponse,
} from "../../../Utils/dashboardFamilyLineNavigation";

/**
 * The order the report opens in — states A–Z, the members A–Z by name
 * inside each — is the report's own, so no sort is lit for it.
 */
const DEFAULT_SORT = "none";

/**
 * Colour + hex, normalised the way the legend builder normalises them, so a row
 * and the legend entry for the same family line land on the same key.
 */
const familyLineKey = (colorName, hexCode) =>
  `${String(colorName || "")
    .trim()
    .toLowerCase()
    .replace(/\s+line$/i, "")}|${String(hexCode || "").toLowerCase()}`;

/**
 * A state as the dropdown files it: the canonical name for one of the 50
 * ("GA" and "georgia" both read as "Georgia"), else as the record spells it.
 */
const toStateFilterValue = (state) => normalizeStateName(state) || state;

function DescByState() {
  const { photos: photoModalPhotos, openPhoto, closePhoto } =
    useDashboardPhotoModal();
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);
  const [searchQuery, setSearchQuery] = useState("");
  // The State dropdown: the ticks being made, the ticks in force, the search
  // box over its list and whether it is open. Ticks only take hold on Apply,
  // as on Family Gatherings.
  const [showStateFilter, setShowStateFilter] = useState(false);
  const [stateSearch, setStateSearch] = useState("");
  const [selectedStates, setSelectedStates] = useState([]);
  const [appliedStates, setAppliedStates] = useState([]);
  const stateRef = useRef(null);
  const [reportLoading, setLoading] = useState(true);
  const [rosterLoading, setRosterLoading] = useState(true);
  // The endpoint's states, as it files them, and the family tree the minors
  // are re-filed by — its members by id, each with the parent they hang under.
  const [reportStates, setReportStates] = useState([]);
  const [treeByMember, setTreeByMember] = useState(() => new Map());
  // The family lines the phone footer key is built from. The report names
  // who heads each row's line itself, but the key spells the lines the way
  // every other report does, off the same statistics payload they read.
  const [legendLines, setLegendLines] = useState([]);
  // The same roster, by member id: the report endpoint sends neither the
  // deceased status, the in-law flag, the multiple-birth fields nor the date
  // of birth, so the markers beside a name and the date two of the same name
  // are split on read that member's roster record.
  const [rosterById, setRosterById] = useState(() => new Map());
  const isCompactLayout = useDashboardPhoneLayout();
  // The phone's footer key comes from the roster, a separate and slower call. A
  // report opened ahead of it showed a short legend that grew moments later, so
  // the phone holds the page for it; a failed roster call still lets it open.
  const loading = reportLoading || (isCompactLayout && rosterLoading);

  // Close the State menu on an outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (stateRef.current && !stateRef.current.contains(e.target)) {
        setShowStateFilter(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // The report and the tree land together, so the list never shows a minor
  // under their own state for a moment before moving to their parent's.
  useEffect(() => {
    let cancelled = false;

    const fetchReport = async () => {
      const family_tree_id = getFamilyTreeIdFromStorage();
      try {
        setLoading(true);
        const [states, roots] = await Promise.all([
          fetchFamilyByState(family_tree_id).catch((error) => {
            console.error("Error fetching family by state:", error);
            return [];
          }),
          fetchDescByStateFamilyTree(family_tree_id).catch((error) => {
            console.error("Error fetching family tree:", error);
            return [];
          }),
        ]);
        if (cancelled) return;
        setReportStates(states);
        setTreeByMember(indexFamilyTreeByMember(roots));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchReport();

    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * The report as the page files it: the endpoint's states with every minor
   * moved under their parent's, and the two totals the heading prints read
   * off that.
   */
  const report = useMemo(() => {
    const states = attachDescByStateSpouses(
      fileMinorsWithParents(reportStates, treeByMember),
      treeByMember
    );
    return { states, ...getDescByStateTotals(states) };
  }, [reportStates, treeByMember]);

  // Settled on its own: a call that failed leaves the report itself on screen
  // with only the name markers and the phone footer key missing.
  useEffect(() => {
    const fetchRoster = async () => {
      try {
        const family_tree_id = getFamilyTreeIdFromStorage();
        if (!family_tree_id) return;

        const response = await getApi(
          `/dashboard-statistics-with-details?family_tree_id=${family_tree_id}`
        );
        if (response?.statusCode !== 200 || !response?.data) return;

        const members = extractAllMembersFromStatisticsResponse(response.data);
        setRosterById(
          new Map(
            members
              .map((member) => [String(member?.id ?? member?.member_id ?? ""), member])
              .filter(([id]) => id)
          )
        );
        setLegendLines(
          buildDashboardColorLegendLines(
            members,
            buildHeadsOfLineIdByColorMap(
              extractHeadsOfLineFromStatisticsResponse(response.data),
              members
            )
          )
        );
      } catch (error) {
        console.error("Error fetching family roster:", error);
      } finally {
        setRosterLoading(false);
      }
    };

    fetchRoster();
  }, []);

  /**
   * The record a row's markers are read from: its roster record where the
   * roster knows it, else the report's own row — which
   * carries none of those fields, so it prints a bare name and reads as a
   * living member, which is all the endpoint returns.
   */
  const getMarkerMember = useCallback(
    (row) =>
      rosterById.get(row.id) ?? {
        ...row.member,
        present_or_deceased: row.member?.present_or_deceased ?? 1,
      },
    [rosterById]
  );

  const isSearching = normalizeDashboardSearchText(searchQuery).length > 0;
  const isStateFiltered = appliedStates.length > 0;

  /* The 50 states, plus anything else the report actually files members
     under, so a state written some other way is still selectable instead of
     silently missing. */
  const stateOptions = useMemo(() => {
    const known = new Set(stateFilterOptions.map((option) => option.value));
    const extras = [];
    report.states.forEach((group) => {
      const value = toStateFilterValue(group.state);
      if (!value || known.has(value)) return;
      known.add(value);
      extras.push({ value, abbr: getStateAbbreviation(value), label: value });
    });
    if (extras.length === 0) return stateFilterOptions;
    return [...stateFilterOptions, ...extras].sort((a, b) =>
      a.value.localeCompare(b.value)
    );
  }, [report.states]);

  const visibleStateOptions = useMemo(() => {
    const term = stateSearch.trim().toLowerCase();
    if (!term) return stateOptions;
    return stateOptions.filter((option) =>
      `${option.label} ${option.abbr || ""}`.toLowerCase().includes(term)
    );
  }, [stateOptions, stateSearch]);

  // What the trigger reads while the filter is applied — "GA, AL +2".
  const stateFilterValue = useMemo(
    () =>
      formatFilterValueList(
        appliedStates.map((name) => getStateAbbreviation(name) || name),
        3
      ),
    [appliedStates]
  );

  /**
   * The states with the rows the State filter and the search leave in them.
   * The filter keeps only the states ticked in the dropdown. A search that
   * matches a state's heading keeps the whole state; otherwise only the rows
   * that match by name or line colour stay, and a state with nothing left in
   * it drops out.
   */
  const visibleStates = useMemo(
    () =>
      report.states
        .filter(
          (group) =>
            !isStateFiltered ||
            appliedStates.includes(toStateFilterValue(group.state))
        )
        .map((group) => {
          if (!isSearching || dashboardSearchMatches(group.state, searchQuery)) {
            return group;
          }

          return {
            ...group,
            rows: group.rows.filter((row) =>
              dashboardSearchMatches(
                [
                  getDashboardMemberSearchText(getMarkerMember(row), row.sortName),
                  row.spouseName,
                  row.colorName,
                  row.headName,
                ].join(" "),
                searchQuery
              )
            ),
          };
        })
        .filter((group) => group.rows.length > 0),
    [
      report.states,
      isStateFiltered,
      appliedStates,
      isSearching,
      searchQuery,
      getMarkerMember,
    ]
  );

  const visibleRowCount = useMemo(
    () =>
      visibleStates.reduce(
        (sum, group) => sum + countDescByStateMembers(group.rows),
        0
      ),
    [visibleStates]
  );

  /**
   * The order the list reads in. With no sort lit — where the page opens —
   * the report's own order stands: A–Z by name inside each state.
   *
   * The states themselves always read A–Z; every sort keeps them where they
   * are and re-orders the members inside each one, so the report stays "by
   * state" whatever is lit.
   */
  const effectiveSort = sortBy;

  const sortedStates = useMemo(() => {
    // Two members of the same name are split by date of birth off the roster
    // record, since the report row carries no age. Unknown dates sort last.
    const compareDob = (a, b, { oldestFirst }) => {
      const left = getMemberDobTimestamp(getMarkerMember(a));
      const right = getMemberDobTimestamp(getMarkerMember(b));
      if (left === right) return 0;
      if (left === -1) return 1;
      if (right === -1) return -1;
      return oldestFirst ? left - right : right - left;
    };
    // The order inside a state when nothing else is asked of it: A–Z by
    // name, two of the same name oldest first.
    const byName = (a, b) =>
      a.sortName.localeCompare(b.sortName) || compareDob(a, b, { oldestFirst: true });

    const compareRows = (() => {
      switch (effectiveSort) {
        case "alpha-asc":
          return byName;
        case "alpha-desc":
          return (a, b) =>
            b.sortName.localeCompare(a.sortName) || compareDob(a, b, { oldestFirst: true });
        // Grouped by line, and each line's members A–Z by name within it.
        case "color":
          return (a, b) => a.colorName.localeCompare(b.colorName) || byName(a, b);
        default:
          return byName;
      }
    })();

    return visibleStates
      .map((group) => ({ ...group, rows: [...group.rows].sort(compareRows) }))
      .sort((a, b) => a.state.localeCompare(b.state));
  }, [visibleStates, effectiveSort, getMarkerMember]);

  const blocks = useMemo(() => toDescByStateBlocks(sortedStates), [sortedStates]);

  // The two-column split, as Family Members does it: the blocks are laid out
  // once more in a hidden measuring list, and the first column takes as many
  // whole blocks as fit the columns' visible height; the rest scroll in the
  // second column. A phone never splits — one scrolling column instead.
  const columnsRef = useRef(null);
  const measureListRef = useRef(null);
  const firstColumnRef = useRef(null);
  const [splitAt, setSplitAt] = useState(0);
  // The split the measurement last asked for. The trim effect below only
  // acts once the rendered split has caught up with it — the commit that
  // swaps the blocks still shows the old split, and trimming that stale
  // column would eat into the fresh measurement one row per render.
  const measuredSplitRef = useRef(0);

  useLayoutEffect(() => {
    if (loading || blocks.length === 0 || isCompactLayout) {
      measuredSplitRef.current = blocks.length;
      setSplitAt(blocks.length);
      return undefined;
    }

    const columnsEl = columnsRef.current;
    const measureEl = measureListRef.current;
    if (!columnsEl || !measureEl) return undefined;

    const calculateSplit = () => {
      const availableHeight = columnsEl.clientHeight;
      const wrappers = Array.from(measureEl.children);
      if (availableHeight <= 0 || wrappers.length === 0) {
        measuredSplitRef.current = blocks.length;
        setSplitAt(blocks.length);
        return;
      }

      let cumulative = 0;
      let nextSplit = wrappers.length;
      for (let index = 0; index < wrappers.length; index += 1) {
        cumulative += wrappers[index].offsetHeight;
        if (cumulative > availableHeight) {
          nextSplit = Math.max(1, index);
          break;
        }
      }

      measuredSplitRef.current = nextSplit;
      setSplitAt(nextSplit);
    };

    calculateSplit();

    // Measured again whenever the visible height changes or the blocks
    // themselves do — a web font landing, or the roster's markers arriving
    // after the report, both re-flow the rows.
    const observer = new ResizeObserver(calculateSplit);
    observer.observe(columnsEl);
    observer.observe(measureEl);
    window.addEventListener("resize", calculateSplit);
    let cancelled = false;
    document.fonts?.ready?.then(() => {
      if (!cancelled) calculateSplit();
    });

    return () => {
      cancelled = true;
      observer.disconnect();
      window.removeEventListener("resize", calculateSplit);
    };
  }, [blocks, loading, isCompactLayout]);

  // A phone is always one column, whatever split a wider window left behind.
  const columns = useMemo(
    () => splitDescByStateBlocks(blocks, isCompactLayout ? blocks.length : splitAt),
    [blocks, splitAt, isCompactLayout]
  );
  const isSingleColumn = columns.length === 1;

  // After render, trim the first column if its last row still runs past the
  // visible area — the shared grid can sit a row taller than the measure.
  useLayoutEffect(() => {
    if (loading || isCompactLayout || isSingleColumn) return;
    if (splitAt !== measuredSplitRef.current) return;

    const columnsEl = columnsRef.current;
    const firstColEl = firstColumnRef.current;
    if (!columnsEl || !firstColEl) return;

    const lastCell = firstColEl.lastElementChild;
    if (!lastCell) return;

    const columnsBottom = columnsEl.getBoundingClientRect().bottom;
    if (lastCell.getBoundingClientRect().bottom > columnsBottom + 0.5) {
      const trimmed = Math.max(1, splitAt - 1);
      measuredSplitRef.current = trimmed;
      setSplitAt(trimmed);
    }
  }, [columns, loading, isCompactLayout, isSingleColumn, splitAt]);

  /**
   * Head of line per colour, from the lines the footer key is built from — so
   * a row whose head the report leaves blank still names the one the key
   * spells. Keyed on colour + hex, with a colour-only fallback for rows whose
   * hex is spelled differently from the heads-of-line record's.
   */
  const headNameByLine = useMemo(() => {
    const byColorAndHex = new Map();
    const byColor = new Map();

    legendLines.forEach(({ headName, colorName, hexCode }) => {
      if (!headName) return;
      byColorAndHex.set(familyLineKey(colorName, hexCode), headName);
      const colorOnly = familyLineKey(colorName, "");
      if (!byColor.has(colorOnly)) byColor.set(colorOnly, headName);
    });

    return { byColorAndHex, byColor };
  }, [legendLines]);

  const getLineHeadName = (row) =>
    row.headName ||
    headNameByLine.byColorAndHex.get(familyLineKey(row.colorName, row.hexCode)) ||
    headNameByLine.byColor.get(familyLineKey(row.colorName, "")) ||
    "";

  /**
   * The State dropdown, as Family Gatherings has it: a trigger that names
   * the states in force, and under it a search box, the states with their
   * abbreviations as tick boxes, then Clear and Apply. Leads the sort bar's
   * row, but sits outside the bar itself, which clips anything that opens
   * past its edge.
   */
  const stateFilterDropdown = (
    <div ref={stateRef} className="selctions-option position-relative desc-by-state-filter">
      <div
        className={filterTriggerClass(
          "selection-drop desc-by-state-filter-trigger",
          Boolean(stateFilterValue)
        )}
        style={{ fontWeight: "bold" }}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedStates(appliedStates);
          setShowStateFilter((prev) => !prev);
        }}
      >
        <FilterTriggerLabel label="State" value={stateFilterValue} />
      </div>
      {showStateFilter && (
        <div className="member-list desc-by-state-filter-list">
          <div className="desc-by-state-filter-search">
            <input
              type="text"
              className="search-input"
              placeholder="Search state..."
              value={stateSearch}
              onChange={(e) => setStateSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          {visibleStateOptions.length === 0 ? (
            <p>No matching state</p>
          ) : (
            visibleStateOptions.map((option) => (
              <div className="member-item" key={option.value}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    margin: 0,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedStates.includes(option.value)}
                    onChange={(e) =>
                      setSelectedStates((prev) =>
                        e.target.checked
                          ? [...prev, option.value]
                          : prev.filter((name) => name !== option.value)
                      )
                    }
                  />
                  <span style={{ flex: 1 }}>{option.label}</span>
                </label>
              </div>
            ))
          )}
          <div className="btn-container">
            <button
              type="button"
              className="btn btn-clear"
              onClick={() => {
                setSelectedStates([]);
                setAppliedStates([]);
                setStateSearch("");
                setShowStateFilter(false);
              }}
            >
              Clear
            </button>
            <button
              type="button"
              className="btn btn-apply"
              onClick={() => {
                setAppliedStates(selectedStates);
                setShowStateFilter(false);
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderRow = (row) => {
    // The markers after the name, by the rules Total Family Members prints
    // them: the asterisk for an in-law who does not start a line, the angel for
    // the deceased, the Twin/Trip/Quad tag for a multiple birth.
    const markerMember = getMarkerMember(row);
    const showInLawAsterisk =
      isDashboardInLawMember(markerMember) &&
      !isExplicitDashboardStartOrTopOfLineMember(markerMember);
    const isDeceased = isDashboardDeceasedMember(markerMember);
    const multipleBirthSup = getMultipleBirthSuperscriptText(markerMember);

    return (
      <React.Fragment key={row.id}>
        <div className="desc-by-state-cell desc-by-state-name">
          <DashboardNameMarkerGroup>
            <DashboardPhotoName
              {...getDashboardMemberNamePhotoUrls(markerMember)}
              onViewPhoto={openPhoto}
            >
              <DashboardMemberNameLabel
                baseName={row.baseName}
                descendantSpouseName={row.descendantSpouseName}
                afterBaseName={
                  showInLawAsterisk && row.descendantSpouseName ? (
                    <DashboardInLawAsterisk />
                  ) : null
                }
              />
            </DashboardPhotoName>
            {showInLawAsterisk && !row.descendantSpouseName && (
              <DashboardInLawAsterisk />
            )}
            {isDeceased && <DashboardDeceasedAngel />}
            {multipleBirthSup && (
              <sup className="dashboard-multiple-birth-sup">{multipleBirthSup}</sup>
            )}
          </DashboardNameMarkerGroup>
          {/* The living spouse beside the descendant, after the descendant's
              own markers so a twin tag stays on the twin. A descendant's
              spouse is always an in-law, so they carry the asterisk. */}
          {row.spouseName && (
            <DashboardNameMarkerGroup>
              <span className="desc-by-state-spouse">{` & ${row.spouseName}`}</span>
              <DashboardInLawAsterisk />
            </DashboardNameMarkerGroup>
          )}
        </div>
        {/* The wide layout has room to name who heads the line right here, so
            it does — and carries no family-line key in its footer. The phone
            layout prints the colour alone and leaves the heads to that key. */}
        <div className="desc-by-state-cell desc-by-state-line">
          <DashboardLineColorName
            colorName={row.colorName}
            hexCode={row.hexCode}
            headName={isCompactLayout ? "" : getLineHeadName(row)}
          />
        </div>
      </React.Fragment>
    );
  };

  /**
   * A state's heading. Spans the grid, so the rows below share one set of
   * columns rather than each aligning to itself. The count is how many rows
   * are under it right now, so a tab or a search that thins a state is
   * reflected in its heading; a heading repeated at the top of the second
   * column says so.
   */
  const renderHeading = (group, continued) => (
    <h4 className="desc-by-state-heading dashboard-section-heading">
      {group.state}
      <span className="desc-by-state-heading-count dashboard-section-heading-count">{` (${countDescByStateMembers(group.rows)})`}</span>
      {continued && <span className="desc-by-state-heading-continued">(cont.)</span>}
    </h4>
  );

  const renderBlock = (block) => (
    <React.Fragment key={block.key}>
      {block.kind === "heading" && renderHeading(block.group, block.continued)}
      {block.kind === "row" && renderRow(block.row)}
    </React.Fragment>
  );

  return (
    <div className="dashboard-container desc-by-state-page dashboard-detail-page">
      <div className="dash-box dashboard-detail-box">
        <div className="dashboard-detail-header">
          <div className="dashboard-detail-header-left">
            <DashboardBackButton />
            <h3 className="dashboard-detail-title">Family by State</h3>
            {/* Two numbers, because a state is not a person: the list holds
                5 states with 72 family members filed under them. With no
                search and no state ticked they are the whole report's — the
                states listed once the minors have moved to their parents',
                and everyone in them; otherwise however many the search or
                the State filter leaves. */}
            <div className="dashboard-detail-count">
              {(() => {
                const isWholeReport = !isSearching && !isStateFiltered;
                const stateCount = isWholeReport
                  ? report.totalStates
                  : visibleStates.length;
                const memberCount = isWholeReport
                  ? report.totalMembers
                  : visibleRowCount;
                // A phone has no room for the words beside the title, so it
                // prints the bare "13/155" instead.
                if (isCompactLayout) return `${stateCount}/${memberCount}`;
                return `${stateCount} ${stateCount === 1 ? "State" : "States"}/${memberCount} ${
                  memberCount === 1 ? "FM" : "FMs"
                }`;
              })()}
            </div>
            {/* The State dropdown leads the bar's row, left of A–Z, in a row
                of its own so it always lands there whatever the header wraps
                — and outside the bar itself, which clips anything that opens
                past its edge. */}
            {!loading && report.states.length > 0 && (
              <div className="desc-by-state-filter-row">
                {stateFilterDropdown}
                {/* Sorts alone: the endpoint lists the living descendants
                    and nothing else, so a member tab could only repeat the
                    whole list or empty it. */}
                <DashboardDetailSortFilterBar
                  showMemberFilters={false}
                  showAgeSorts={false}
                  sortBy={sortBy}
                  onSortChange={setSortBy}
                />
                {/* The search box takes its own line above the trigger and
                    bar at every width (see App.css). On a desktop the pair
                    slides over the column rule, centred on the report. */}
                <DashboardDetailSearchBox
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search name, state, line color"
                  ariaLabel="Search by name, state or line color"
                />
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="dashboard-detail-empty-state">
            <p>Loading descendants by state...</p>
          </div>
        ) : sortedStates.length === 0 ? (
          <div className="dashboard-detail-empty-state">
            {isSearching ? (
              <DashboardDetailSearchNoMatch query={searchQuery} />
            ) : isStateFiltered ? (
              <p>No family members born in the selected states.</p>
            ) : (
              <p>No data for this period.</p>
            )}
          </div>
        ) : (
          <div className="desc-by-state-page dashboard-detail-content-wrapper">
            {/* Hidden copy of every block, laid out once each so the split
                can be measured against the visible height. */}
            {!isCompactLayout && (
              <div
                ref={measureListRef}
                className="desc-by-state-list-measure"
                aria-hidden="true"
              >
                {blocks.map((block) => (
                  <div key={block.key} className="desc-by-state-list dashboard-detail-list">
                    {renderBlock(block)}
                  </div>
                ))}
              </div>
            )}

            <div ref={columnsRef} className="desc-by-state-columns">
              <div
                ref={firstColumnRef}
                className={`desc-by-state-list dashboard-detail-list desc-by-state-list-first${
                  isSingleColumn ? " desc-by-state-list--scrollable" : ""
                }`}
              >
                {columns[0].map(renderBlock)}
              </div>
              {!isSingleColumn && (
                <>
                  <div className="desc-by-state-column-divider" />
                  <div className="desc-by-state-list dashboard-detail-list desc-by-state-list-scroll">
                    {columns[1].map(renderBlock)}
                  </div>
                </>
              )}
            </div>

            <div className="desc-by-state-page in-law-footer">
              {/* The multiple-birth tags the key lists are the ones on the rows
                  above it, as on Total Family Members. */}
              <DashboardMarkerLegend
                multipleBirthTags={getMultipleBirthLegendTags(
                  sortedStates.flatMap((group) => group.rows.map(getMarkerMember))
                )}
              />
              {/* Family lines are a phone-only key, as on the sibling reports:
                  the wide layout has room to name a line on the row itself. */}
              {isCompactLayout && <DashboardColorLegend lines={legendLines} />}
            </div>
          </div>
        )}
      </div>

      <DashboardPhotoModal photos={photoModalPhotos} onClose={closePhoto} />
    </div>
  );
}

export default DescByState;
